import { AnimatePresence, motion } from 'motion/react';
import type { Variants } from 'motion/react';
import type { ReactNode, RefObject } from 'react';
import { BreathingLogo } from '../BreathingLogo';
import { CountdownTimer } from '../CountdownTimer';
import { TwitchPromo } from './TwitchPromo';
import {
  AIR_DATE_LABEL,
  AIR_TIME_LABEL,
  ENTER_TO_WIN_ENABLED,
  watchUrlForPhase,
} from '../../content/site';
import type { EventPhase, EventStateView } from '../../lib/eventState';
import {
  fadeRise,
  lineDraw,
  spring,
  staggerGroup,
  starPop,
  useMotionEnabled,
} from '../../lib/motion';
import { plausibleEventClass } from '../../lib/plausible';

/**
 * Home hero — branches on the show phase:
 *   PRE  → scoreboard countdown + air date
 *   LIVE → "LIVE NOW" + Twitch CTA (moved into the hero per the 7.27 dev
 *          frames, HX-36) + Enter-to-Win CTA (feature-flagged until wired)
 *   POST → "WATCH", linking the admin-set watch URL
 * (POST had no Figma design — built as the live-page variant, pending
 * designer sign-off.)
 *
 * Arrival choreography (2026-07-24): the phase's state block ENTERS instead
 * of popping — the logo renders immediately (tiny SVGs), and when
 * /event/state resolves the state element stagger-reveals (countdown boots
 * from 00s, LIVE NOW's glow lines draw outward into their stars). The same
 * choreography plays when the poll flips the phase mid-session, so going
 * live is a moment rather than a jump cut. Reduced motion renders each
 * state statically with instant swaps.
 *
 * Logo: single lockup on ≥sm; below sm the designed mobile split (HyperX
 * LEVEL piece stacked over the AWAKENING mark) keeps the mark legible.
 */

/** Pass-through for variant propagation: participates in the parent's
 *  stagger order without animating anything itself. */
const passThrough: Variants = { hidden: {}, visible: {} };

// lineDraw / starPop moved to lib/motion (HX-48) — the Call-to-Rise WATCH NOW
// CTA runs the same line-into-star entrance, in red.
const headingIn: Variants = {
  hidden: { opacity: 0, scale: 0.94 },
  visible: { opacity: 1, scale: 1, transition: spring.reveal },
};

/**
 * Hero heading flanked by white glow lines capped with white spark stars at
 * the OUTER ends (per the live dev frame: each side is line→star with the
 * left group rotated 180°). Pure white line + star-white.svg — deliberately
 * brighter than the gray #8c8c8c break-line dividers further down the page.
 * The line tucks -10px under the star (design overlap) so they connect.
 *
 * Entrance: the heading settles in while each line DRAWS outward from the
 * text toward its star (transform-origin at the text side), and the stars
 * pop last — the neon circuit completing. Variants inherit from the phase
 * block; with reduced motion there is no motion parent, so it's static.
 */
function GlowLineHeading({ text }: { text: string }) {
  return (
    <div className="flex w-full items-center justify-center gap-[clamp(0.5rem,2vw,1.25rem)] px-5">
      <div className="hidden min-w-4 max-w-[1200px] flex-1 items-center min-[420px]:flex">
        {/* starPop lives on the SPAN, never the img: the img's rotate-90
            class is a transform, and a motion.img would overwrite it with
            its own inline transform while animating scale. */}
        <motion.span
          variants={starPop}
          className="relative z-10 flex h-[27px] w-[46px] shrink-0 items-center justify-center"
        >
          <img
            src="/assets/images/ornaments/star-white.svg"
            alt=""
            className="glow-white-md h-[46px] w-[27px] max-w-none rotate-90"
          />
        </motion.span>
        <motion.div
          variants={lineDraw}
          className="ml-[-10px] h-[2px] w-full origin-right bg-white shadow-[0px_0px_20px_0px_rgba(255,255,255,0.5)]"
        />
      </div>
      <motion.p
        variants={headingIn}
        className="text-glow text-center font-display leading-none text-brand-white [font-size:clamp(2.6rem,7.8vw,9.4rem)]"
      >
        {text}
      </motion.p>
      <div className="hidden min-w-4 max-w-[1200px] flex-1 items-center min-[420px]:flex">
        <motion.div
          variants={lineDraw}
          className="mr-[-10px] h-[2px] w-full origin-left bg-white shadow-[0px_0px_20px_0px_rgba(255,255,255,0.5)]"
        />
        <motion.span
          variants={starPop}
          className="relative z-10 flex h-[27px] w-[46px] shrink-0 items-center justify-center"
        >
          <img
            src="/assets/images/ornaments/star-white.svg"
            alt=""
            className="glow-white-md h-[46px] w-[27px] max-w-none rotate-90"
          />
        </motion.span>
      </div>
    </div>
  );
}

function PresentedBy() {
  return (
    <div className="flex flex-col items-center gap-[5px]">
      <p className="font-body text-[clamp(0.85rem,1.05vw,1.25rem)] text-brand-muted">
        Presented by
      </p>
      <img
        src="/assets/images/logos/amd-pc-game-pass.svg"
        alt="AMD and PC Game Pass"
        className="w-[clamp(12.1rem,14.1vw,16.9rem)]"
      />
    </div>
  );
}

function EnterToWinButton() {
  // Display-only for now — the CTA is on the page but NOT wired to the
  // registration flow yet (M2, HX-8). Becomes a link when the flow ships;
  // default/hover states crossfade like the Twitch button.
  //
  // The Plausible tag is already on it: the tagged-events extension binds to
  // any element carrying the class, not just anchors, so clicks on the
  // unwired button are counted too — which is exactly the number worth
  // having (people trying to enter before the CTA goes anywhere). It keeps
  // working unchanged when this becomes a link to /sweepstakes.
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      transition={spring.tap}
      className={`group relative mt-3 h-[clamp(3rem,3.5vw,4.25rem)] ${plausibleEventClass(
        'CTA Click',
        { location: 'enter-to-win' },
      )}`}
      style={{ aspectRatio: '275 / 68' }}
    >
      <img
        src="/assets/images/buttons/enter-to-win.svg"
        alt="Enter to win"
        className="absolute inset-0 h-full w-full transition-opacity duration-200 group-hover:opacity-0"
      />
      <img
        src="/assets/images/buttons/enter-to-win-hover.svg"
        alt=""
        className="glow-white-sm absolute inset-0 h-full w-full opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      />
    </motion.div>
  );
}

/** One item in the phase block's stagger. Inert without a motion parent
 *  (the reduced-motion path renders the block as a plain div). */
function Item({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <motion.div variants={fadeRise} className={className}>
      {children}
    </motion.div>
  );
}

function PhaseContent({
  phase,
  event,
  stateRef,
}: {
  phase: EventPhase;
  event: EventStateView;
  stateRef?: RefObject<HTMLDivElement>;
}) {
  if (phase === 'PRE') {
    return (
      <>
        <motion.div ref={stateRef} variants={passThrough} className="w-full">
          <CountdownTimer
            showStartAt={event.showStartAt}
            serverOffsetMs={event.serverOffsetMs}
            boot
          />
        </motion.div>
        {/* Date + time read as one lockup — tight leading, time at
            ~0.65× the date size, per the 7.17 mock. */}
        <Item className="flex flex-col items-center">
          <p className="text-glow font-heading leading-tight text-[clamp(1.15rem,1.9vw,2.25rem)] uppercase tracking-wide text-brand-white">
            {AIR_DATE_LABEL}
          </p>
          <p className="text-glow font-heading leading-tight text-[clamp(0.8rem,1.25vw,1.5rem)] uppercase tracking-wide text-brand-white">
            {AIR_TIME_LABEL}
          </p>
        </Item>
        <Item>
          <PresentedBy />
        </Item>
      </>
    );
  }
  if (phase === 'LIVE') {
    return (
      <>
        <motion.div ref={stateRef} variants={passThrough} className="w-full">
          <GlowLineHeading text="LIVE NOW" />
        </motion.div>
        <Item className="w-full">
          {/* Live: the admin's watch URL if set, else the channel — the
              schedule deep-link is only useful before the show (HX-44). */}
          <TwitchPromo
            compact
            location="live-hero"
            href={watchUrlForPhase(phase, event.watchUrl)}
          />
        </Item>
        <Item>
          <PresentedBy />
        </Item>
        {ENTER_TO_WIN_ENABLED && (
          <Item>
            <EnterToWinButton />
          </Item>
        )}
      </>
    );
  }
  return (
    <>
      <motion.div ref={stateRef} variants={passThrough} className="w-full">
        {/* Always the word "WATCH", always a link (client 2026-08-17). It used
            to read "WATCH THE REPLAY" only when a URL was set and go dead
            otherwise; now watchUrlForPhase always resolves to something, so the
            heading never dead-ends and the copy doesn't change under the
            client's feet when the VOD lands. */}
        <a
          href={watchUrlForPhase(phase, event.watchUrl)}
          aria-label="Watch (opens in a new tab)"
          className={`block ${plausibleEventClass('CTA Click', { location: 'hero-replay' })}`}
          target="_blank"
          rel="noreferrer"
        >
          <GlowLineHeading text="WATCH" />
        </a>
      </motion.div>
      <Item>
        <PresentedBy />
      </Item>
    </>
  );
}

export function HeroSection({
  event,
  stateRef,
}: {
  event: EventStateView | null;
  /** Wraps the phase's state element (timer / LIVE NOW / WATCH) — the
   *  AppBar's scroll trigger + FLIP measurement anchor. */
  stateRef?: RefObject<HTMLDivElement>;
}) {
  const motionOn = useMotionEnabled();
  const phase = event?.phase ?? null;

  // [gap:inherit] keeps the block's internal rhythm identical to the column
  // it sits in, so the wrapper div is layout-invisible.
  const blockClass = 'flex w-full flex-col items-center [gap:inherit]';

  return (
    <section className="relative flex w-full flex-col items-center justify-center overflow-hidden py-10">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 bg-black">
        <img
          src="/assets/images/backgrounds/bg-top.webp"
          alt=""
          fetchpriority="high"
          className="h-full w-full object-contain object-center"
        />
      </div>

      <div className="flex w-full max-w-content flex-col items-center gap-[clamp(0.75rem,2svh,1.5rem)] px-5">
        {/* Logo lockup — renders immediately (tiny SVGs) while the state
            block waits on /event/state. Breathing red glow lives in
            BreathingLogo (composited layers, not filter keyframes). */}
        <div className="flex w-full flex-col items-center">
          <BreathingLogo
            src="/assets/images/logos/level-awakening.svg"
            alt="HyperX Level Awakening"
            className="hidden w-[min(100%,853px)] sm:block"
          />
          <div className="flex w-full flex-col items-center gap-3 sm:hidden">
            <img
              src="/assets/images/logos/hyper-level.svg"
              alt="HyperX Level"
              className="glow-white-sm w-[180px]"
            />
            <BreathingLogo
              src="/assets/images/logos/awakening.svg"
              alt="Awakening"
              className="w-full max-w-[420px]"
            />
          </div>
        </div>

        {motionOn ? (
          <AnimatePresence mode="wait">
            {phase && event && (
              <motion.div
                key={phase}
                variants={staggerGroup}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0, transition: { duration: 0.25 } }}
                className={blockClass}
              >
                <PhaseContent phase={phase} event={event} stateRef={stateRef} />
              </motion.div>
            )}
          </AnimatePresence>
        ) : (
          phase &&
          event && (
            <div className={blockClass}>
              <PhaseContent phase={phase} event={event} stateRef={stateRef} />
            </div>
          )
        )}
      </div>
    </section>
  );
}
